import React, { useMemo, useState, useCallback } from 'react';
import { Text, View, TouchableOpacity } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Avatar from '@/components/People/Avatar';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateAssignees, updateAssigneeResponse, updateTaskCompletionStatus, getAssigneesNeeded, getConflictingTaskTitles } from '@/data/tasks';
import { Query } from '@/utils/react-query/Queries';
import { invalidateServiceFlowForTaskInstance } from '@/utils/serviceFlow';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { useRouter } from 'expo-router';
import SortableList from '@/components/General/SortableList';
import Unicon from '@/components/General/Unicon';
import FeatureIcon from '@/components/General/FeatureIcon';
import { usePressBlockerStore } from '@/data/store/usePressBlockerStore';
import Toast from 'react-native-toast-message';
import SheetModal from '@/components/General/SheetModal';
import Input from '@/components/General/inputs/Input';
import InputSection from '@/components/General/inputs/InputSection';
import SolidButton from '@/components/General/SolidButton';
import CutCard from '@/components/General/CutCard';
import {Tooltip} from '../General/Tooltip';
import Toggle from '@/components/General/inputs/Toggle';
import useTaskInstanceLocation from '@/utils/hooks/useTaskInstanceLocation';
import { isAutomationDueType, isFollowUpDueType } from '@/utils/constants/taskTypes';
import { getTaskPurposeIcon, getTaskPurposeLabel } from '@/utils/constants/followUp';
import Chip from '@/components/General/Chip';

const TaskCard = props => {
    // fromScreen: origin path forwarded to SheetSelector add-screens for their
    // dismissTo return. Callers pass a literal — a usePathname() subscription
    // here would re-render every mounted card on every navigation.
    const { taskInstance, showEvent = true, actions = [], variant = 'default', personID = null, fromScreen = null } = props;
    const locationName = useTaskInstanceLocation(taskInstance);

    const [openAssigneePositionsModal, setOpenAssigneePositionsModal] = useState(false);
    const [pendingAssigneeUpdate, setPendingAssigneeUpdate] = useState({});
    const [declineModalOpen, setDeclineModalOpen] = useState(false);
    const [declineReason, setDeclineReason] = useState("");
    const [forwardTo, setForwardTo] = useState(null);
    const [notifyAll, setNotifyAll] = useState(false);

    const router = useRouter();
    const queryClient = useQueryClient();

    const now = new Date();
    const isCancelled = !!taskInstance?.cancelledAt;
    // AUTOMATIONS and FOLLOW_UP never auto-complete on a lapsed date (§10.7);
    // cancelled rows are terminal, never overdue.
    const isOverdue = !isCancelled
        && !(taskInstance?.Task?.assumeCompleted && !isAutomationDueType(taskInstance?.Task?.dueType) && !isFollowUpDueType(taskInstance?.Task?.dueType))
        && !taskInstance?.completed
        && taskInstance?.dueDate < now.toISOString();
    const isCompleted = taskInstance?.completed;
    // Care instances carry followUpContext from the Follow Up queries; a bare
    // FOLLOW_UP dueType is also a care task — either way, the quick-complete
    // path must route to the occurrence screen's follow_up_complete_task
    // flow, never the direct completed=true write the server rejects.
    const isCareTask = !!taskInstance?.followUpContext || isFollowUpDueType(taskInstance?.Task?.dueType);

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: recentAssignees } = Query.tasks.recentAssignees({
        taskID: taskInstance?.taskID,
        options: { enabled: !!taskInstance?.taskID },
    });
    // Team members (via the team's linked role) are pinned to the top of the
    // assignee picker — fetched by team id rather than bloating every
    // taskInstance payload with the role's roster.
    const { data: assigneeTeam } = Query.taskTeams.byId({
        id: taskInstance?.VolunteerTeam?.id,
        options: { enabled: !!taskInstance?.VolunteerTeam?.id },
    });

    const updateAssigneesMutation = useMutation({
        mutationFn: updateAssignees,
        onSuccess: (data) => {
            data.forEach(a => {
                queryClient.invalidateQueries({ queryKey: QueryKeys.people.byId(a.personID) });
            });
            queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.byId(taskInstance?.taskID) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.byId(taskInstance?.eventInstanceID) });
            if (taskInstance?.EventInstance?.eventID) {
                queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.upcomingVolunteerTasks(taskInstance.EventInstance.eventID) });
            }
            queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.upcomingTasks(loggedInUser?.id) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.upcomingTaskAssignments(loggedInUser?.id) });
            queryClient.invalidateQueries({ queryKey: ['upcomingTeamTaskInstances'] });
            queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
            queryClient.invalidateQueries({ queryKey: QueryKeys.taskInstances.individual() });
            // Parent Service Flow: task rows are nested inside the flow query —
            // assignment/response/completion changes also update the date's
            // protection status (spec §12.5).
            invalidateServiceFlowForTaskInstance(queryClient, {
                eventInstanceID: taskInstance?.eventInstanceID,
                organizationID: loggedInUser?.organizationID,
                accountID: loggedInUser?.accountID,
            });
        }
    });

    const updateAssigneeResponseMutation = useMutation({
        mutationFn: updateAssigneeResponse,
        onSuccess: (data) => {
            if(data) Toast.show({ type: 'success', text2: 'Response updated!' });
            queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.byId(taskInstance?.taskID) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.byId(taskInstance?.eventInstanceID) });
            if (taskInstance?.EventInstance?.eventID) {
                queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.upcomingVolunteerTasks(taskInstance.EventInstance.eventID) });
            }
            queryClient.invalidateQueries({ queryKey: QueryKeys.people.byId(loggedInUser?.id) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.upcomingTasks(loggedInUser?.id) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.upcomingTaskAssignments(loggedInUser?.id) });
            queryClient.invalidateQueries({ queryKey: ['upcomingTeamTaskInstances'] });
            queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
            queryClient.invalidateQueries({ queryKey: QueryKeys.taskInstances.individual() });
            // Parent Service Flow: task rows are nested inside the flow query —
            // assignment/response/completion changes also update the date's
            // protection status (spec §12.5).
            invalidateServiceFlowForTaskInstance(queryClient, {
                eventInstanceID: taskInstance?.eventInstanceID,
                organizationID: loggedInUser?.organizationID,
                accountID: loggedInUser?.accountID,
            });
        },
        onError: () => {
            Toast.show({ type: 'error', text2: 'Failed to send your response... Please try again later' });
        },
    });

    const completionStatusMutation = useMutation({
        mutationFn: updateTaskCompletionStatus,
        onMutate: async ({ completed }) => {
            if (!personID) return undefined;

            const personQueryKey = QueryKeys.people.byId(personID);
            await queryClient.cancelQueries({ queryKey: personQueryKey });
            const previousPerson = queryClient.getQueryData(personQueryKey);

            queryClient.setQueryData(personQueryKey, currentPerson => {
                if (!currentPerson?.TaskInstances) return currentPerson;
                return {
                    ...currentPerson,
                    TaskInstances: completed
                        ? currentPerson.TaskInstances.filter(instance => instance.id !== taskInstance?.id)
                        : currentPerson.TaskInstances.map(instance => (
                            instance.id === taskInstance?.id ? { ...instance, completed } : instance
                        )),
                };
            });

            return { previousPerson, personQueryKey };
        },
        onSuccess: () => {
            Toast.show({ type: 'success', text2: 'Task marked as completed!' });
            queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.byId(taskInstance?.taskID) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.taskInstances.byId(taskInstance?.id) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.byId(taskInstance?.eventInstanceID) });
            if (taskInstance?.EventInstance?.eventID) {
                queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.upcomingVolunteerTasks(taskInstance.EventInstance.eventID) });
            }
            if (personID) {
                queryClient.invalidateQueries({ queryKey: QueryKeys.people.byId(personID) });
                queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.personHistory(personID) });
            }
            queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.upcomingTasks(loggedInUser?.id) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.upcomingTaskAssignments(loggedInUser?.id) });
            queryClient.invalidateQueries({ queryKey: ['upcomingTeamTaskInstances'] });
            queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
            queryClient.invalidateQueries({ queryKey: QueryKeys.taskInstances.individual() });
            // Parent Service Flow: task rows are nested inside the flow query —
            // assignment/response/completion changes also update the date's
            // protection status (spec §12.5).
            invalidateServiceFlowForTaskInstance(queryClient, {
                eventInstanceID: taskInstance?.eventInstanceID,
                organizationID: loggedInUser?.organizationID,
                accountID: loggedInUser?.accountID,
            });
        },
        onError: (_error, _variables, context) => {
            if (context?.previousPerson) queryClient.setQueryData(context.personQueryKey, context.previousPerson);
            Toast.show({ type: 'error', text2: 'Failed to update completion status... Please try again later' });
        },
    });

    const handleMarkCompleted = useCallback(() => {
        if (isCareTask) {
            // Care tasks complete through follow_up_complete_task on the
            // occurrence screen (structured result per purpose).
            router.push({ pathname: "/tasks/instances/" + taskInstance?.id });
            return;
        }
        completionStatusMutation.mutate({ id: taskInstance?.id, completed: true });
    }, [completionStatusMutation, taskInstance?.id, isCareTask, router]);

    // Path task instances have no volunteer team — path editors can reassign.
    const isCurrentUserAssigner = loggedInUser?.isAdmin || taskInstance?.Task?.dueType === 'INDIVIDUAL' || (isAutomationDueType(taskInstance?.Task?.dueType) && loggedInUser?.can?.("Update Paths")) || taskInstance?.VolunteerTeam?.VolunteerTeamAssigners?.some(a => a.personID === loggedInUser?.id);
    const isCurrentUserAssignee = taskInstance?.Assignees?.some(a => a?.id === loggedInUser?.id);
    const currentUserAssignment = taskInstance?.Assignees?.find(a => a?.id === loggedInUser?.id);

    const sortedAssignees = useMemo(() => {
        // Copy first — sorting in place would mutate the shared query-cached
        // taskInstance object (and is a React Compiler violation).
        return [...(taskInstance?.Assignees || [])].sort((a, b) => {
            let aValue = (a?.assignmentResponse === "DECLINED" ? -2 : 0) + (a.position < b.position ? 1 : 0);
            let bValue = (b?.assignmentResponse === "DECLINED" ? -2 : 0) + (b.position < a.position ? 1 : 0);
            return aValue - bValue;
        }) || [];
    }, [taskInstance?.Assignees]);

    const assigneeAvatars = useMemo(() => {
        const maxVisible = 3;
        const visibleAssignees = sortedAssignees.slice(0, maxVisible);
        const remainingCount = sortedAssignees.length - maxVisible;

        return (
            <View style={styles.avatarStack}>
                {visibleAssignees.reverse().map((item, i) => (
                    <View key={item.id} style={[styles.avatarWrapper, { marginLeft: i > 0 ? -19 : 0, zIndex: maxVisible - i }]}>
                        <Avatar
                            size={40}
                            profile={item}
                            borderColor={item.assignmentResponse === "ACCEPTED" ? "success" : item.assignmentResponse === "DECLINED" ? "error" : "warning"}
                        />
                    </View>
                ))}
                {remainingCount > 0 && (
                    <View style={[styles.moreAvatars, { marginLeft: -12 }]}>
                        <Text style={styles.moreAvatarsText}>+{remainingCount}</Text>
                    </View>
                )}
            </View>
        );
    }, [sortedAssignees]);

    const emptyAssigneeSlot = useMemo(() => (
        <View style={styles.emptyAvatar(isCurrentUserAssigner)}>
            <Unicon name={isCurrentUserAssigner ? "person-add-outline" : "person-outline"} size={20} colorVariant={"textSecondary"} />
        </View>
    ), [isCurrentUserAssigner]);

    const hasConflicts = useMemo(() => {
        return taskInstance?.Assignees?.some(a =>
            taskInstance?.EventInstance?.TaskInstances?.some(ti =>
                taskInstance?.id !== ti.id && ti.Assignees?.some(tia => a.id === tia.id)
            )
        );
    }, [taskInstance]);

    // Per-assignee conflict lines for the Conflict chip tooltip
    // e.g. "John conflicts with: Blender\nJane conflicts with: Hostess, Usher"
    const conflictTooltipText = useMemo(() => {
        const lines = [];
        taskInstance?.Assignees?.forEach(a => {
            const titles = getConflictingTaskTitles(taskInstance, a.id);
            if(titles.length > 0){
                const name = [a?.firstName, a?.lastName].filter(Boolean).join(' ') || a?.id;
                lines.push(`${name} conflicts with: ${titles.join(', ')}`);
            }
        });
        return lines.join('\n');
    }, [taskInstance]);

    // Ordered assignee names for the assignee avatars tooltip (long press / hover).
    // Reversed to match the avatar stack's left-to-right visual order.
    const assigneeNamesTooltip = useMemo(() => {
        if(!sortedAssignees?.length) return null;
        return [...sortedAssignees].reverse().map(a => [a?.firstName, a?.lastName].filter(Boolean).join(' ')).join('\n');
    }, [sortedAssignees]);

    const assigneesNeeded = getAssigneesNeeded(taskInstance);
    const currentAssignees = taskInstance?.Assignees?.filter(assignee => assignee?.assignmentResponse !== "DECLINED").length || 0;
    const needsMoreAssignees = currentAssignees < assigneesNeeded;

    const handleSelfAssign = useCallback(() => {
        updateAssigneesMutation.mutate({
            taskInstanceID: taskInstance?.id,
            existingAssignees: taskInstance?.Assignees,
            newAssignees: [{ ...loggedInUser, position: taskInstance?.Assignees?.length }],
            taskInstance: taskInstance,
            currentUser: loggedInUser?.id,
            assignmentResponse: "ACCEPTED"
        });
    }, [taskInstance, loggedInUser, updateAssigneesMutation]);

    const handleCardPress = useCallback(() => {
        if (usePressBlockerStore.getState().blocked) return;
        router.push({ pathname: "/tasks/instances/" + taskInstance?.id });
    }, [router, taskInstance?.id]);

    const handleEventPress = useCallback(() => {
        router.push({ pathname: "/events/instances/" + taskInstance?.EventInstance?.id });
    }, [router, taskInstance?.EventInstance?.id]);

    const handleAssigneeChange = useCallback((selected_items) => {
        const pendingUpdate = {
            taskInstanceID: taskInstance?.id,
            existingAssignees: taskInstance?.Assignees,
            newAssignees: selected_items,
            taskInstance: taskInstance,
            currentUser: loggedInUser?.id
        };

        if (selected_items.length > assigneesNeeded) {
            setNotifyAll(false);
            setPendingAssigneeUpdate(pendingUpdate);
            setTimeout(() => setOpenAssigneePositionsModal(true), 500);
        } else {
            updateAssigneesMutation.mutate(pendingUpdate);
        }
    }, [taskInstance, loggedInUser, assigneesNeeded, updateAssigneesMutation]);

    const handleAccept = useCallback(() => {
        if (!currentUserAssignment) return;
        updateAssigneeResponseMutation.mutate({
            personID: currentUserAssignment.id,
            taskInstanceID: taskInstance?.id,
            newResponse: "ACCEPTED",
            reason: null,
            forwardToID: null,
            loggedInUserID: loggedInUser?.id
        });
    }, [currentUserAssignment, taskInstance, loggedInUser, updateAssigneeResponseMutation]);

    const handleDeclinePress = useCallback(() => {
        setDeclineReason("");
        setForwardTo(null);
        setDeclineModalOpen(true);
    }, []);

    const handleDeclineConfirm = useCallback(() => {
        updateAssigneeResponseMutation.mutate({
            personID: currentUserAssignment.id,
            taskInstanceID: taskInstance?.id,
            newResponse: "DECLINED",
            reason: declineReason,
            forwardToID: forwardTo?.id || null,
            loggedInUserID: loggedInUser?.id
        });
        setDeclineModalOpen(false);
    }, [declineReason, forwardTo, currentUserAssignment, taskInstance, loggedInUser, updateAssigneeResponseMutation]);

    const getStatusIcon = () => {
        // Cancelled ≠ completed — it's its own terminal state everywhere.
        if (isCancelled) return { name: "close-circle", colorVariant: "textSecondary", message: "This task was cancelled" };
        if (isCompleted) return { name: "checkmark-circle", colorVariant: "success", message: "This task has been completed" };
        if (isOverdue) return { name: "alert-circle", colorVariant: "error", message: "This task is overdue!" };
        return { name: "clipboard-text-clock", library: "material", colorVariant: "textSecondary", message: "This task is pending completion" };
    };

    const statusIcon = getStatusIcon();
    const eventName = taskInstance?.EventInstance?.nameOverride || taskInstance?.EventInstance?.Event?.name;
    const eventColor = taskInstance?.EventInstance?.Event?.Calendar?.color;
    const eventDate = taskInstance?.EventInstance?.startTime ? new Date(taskInstance?.EventInstance?.startTime) : null;
    const dueDate = taskInstance?.dueDate ? new Date(taskInstance.dueDate) : eventDate;
    const isValidDueDate = dueDate && !isNaN(dueDate);
    const teamName = taskInstance?.VolunteerTeam?.name;
    const taskTime = taskInstance?.time ? new Date(taskInstance.time) : null;

    const showEventInfo = taskInstance?.EventInstance && showEvent;
    const isPathTask = isAutomationDueType(taskInstance?.Task?.dueType);
    const { data: enrollmentContext } = Query.paths.enrollmentContext({
        taskInstanceID: taskInstance?.id,
        options: { enabled: isPathTask && !!taskInstance?.id },
    });
    const enrollee = enrollmentContext?.PathEnrollment?.Person;
    const isTrainingTask = taskInstance?.Task?.dueType === "TRAINING";
    const { data: trainingClassInstance } = Query.trainingClassInstances.byTaskInstanceId({
        id: taskInstance?.id,
        options: { enabled: isTrainingTask }
    });
    const showTrainingInfo = isTrainingTask && trainingClassInstance;
    const handleTrainingClassPress = useCallback(() => {
        const moduleID = trainingClassInstance?.Class?.Module?.id;
        const instanceID = trainingClassInstance?.id;
        if (moduleID && instanceID) router.push(`/training/${moduleID}/instance/${instanceID}`);
    }, [router, trainingClassInstance]);
    const showResponseButtons = currentUserAssignment && !isCompleted && !isCancelled;
    const showSelfAssign = !currentUserAssignment && taskInstance?.Task?.allowSelfAssignment && !isCompleted && !isCancelled && !isCurrentUserAssignee && !isCareTask;
    const showButtons = showResponseButtons || showSelfAssign || actions.length > 0;

    if (!taskInstance) return null;

    // 'embedded' drops all card chrome — for nesting inside another card
    // (e.g. a Service Flow timeline row's details card).
    const embedded = variant === 'embedded';
    const CardContainer = variant === 'default' ? CutCard : View;

    return (
        <>
            <CardContainer
                testID={`TaskCard-${taskInstance.id}`}
                style={styles.card(variant)}
            >
                <View style={styles.cardInner(embedded)}>
                    <View style={styles.recordHeader}>
                        <FeatureIcon feature="tasks" size={28} iconSize={16} />
                        <View style={styles.headerText}>
                            <View style={styles.statusLine}>
                                <Tooltip title={statusIcon.message}>
                                    <View style={styles.inlineMetadata}>
                                        <Unicon name={statusIcon.name} library={statusIcon.library} size={13} colorVariant={statusIcon.colorVariant} />
                                        <Text style={styles.statusText(statusIcon.colorVariant)}>
                                            {isCancelled ? 'Cancelled' : isCompleted ? 'Completed' : isOverdue ? 'Overdue' : 'Pending'}
                                        </Text>
                                    </View>
                                </Tooltip>
                                {isValidDueDate && (
                                    <Text style={styles.dueText}>
                                        Due {dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </Text>
                                )}
                            </View>
                            <TouchableOpacity testID={`TaskCard-open-${taskInstance.id}`} onPress={handleCardPress} activeOpacity={0.7} style={styles.titleLine}>
                                <Text style={[styles.taskTitle, isCompleted && styles.taskTitleCompleted]}>
                                    {taskInstance?.Task?.title}
                                </Text>
                                <Unicon name="chevron-forward" size={16} colorVariant="textSecondary" />
                            </TouchableOpacity>
                        </View>
                    </View>
                    <View style={styles.contentRow}>
                        <View style={styles.leftContent}>
                            {isPathTask && enrollee && (
                                <TouchableOpacity
                                    onPress={() => router.push(`/people/${enrollee.id}`)}
                                    activeOpacity={0.7}
                                    style={styles.enrolleeRow}
                                    testID="TaskCardEnrollee"
                                >
                                    <Avatar size={22} profile={enrollee} disableBorder />
                                    <Text style={styles.enrolleeText}>
                                        For {enrollee.firstName} {enrollee.lastName}
                                    </Text>
                                </TouchableOpacity>
                            )}

                            <View style={styles.metadataRow}>
                                {isFollowUpDueType(taskInstance?.Task?.dueType) && (
                                    <Chip
                                        testID={`TaskCard-followup-badge-${taskInstance.id}`}
                                        icon={getTaskPurposeIcon(taskInstance?.followUpContext?.purpose)}
                                        colorVariant="paths"
                                    >
                                        {taskInstance?.followUpContext?.purpose
                                            ? `Follow Up · ${getTaskPurposeLabel(taskInstance.followUpContext.purpose)}`
                                            : 'Follow Up'}
                                    </Chip>
                                )}
                                {!!locationName && (
                                    <View style={styles.inlineMetadata}>
                                        <Unicon name="location-outline" size={14} colorVariant="textSecondary" />
                                        <Text style={styles.metadataText} testID={`TaskCard-location-${taskInstance.id}`}>{locationName}</Text>
                                    </View>
                                )}
                                {teamName && (
                                    taskInstance?.VolunteerTeam?.id ? (
                                        <TouchableOpacity testID={`TaskCard-team-${taskInstance.id}`} accessibilityRole="button" accessibilityLabel={`Open ${teamName} team`} onPress={() => router.push(`/tasks/teams/${taskInstance.VolunteerTeam.id}`)} style={styles.inlineMetadata}>
                                            <Unicon name="people-outline" size={14} colorVariant="tasks" />
                                            <Text style={[styles.metadataText, styles.metadataLinkText]}>{teamName}</Text>
                                        </TouchableOpacity>
                                    ) : (
                                        <View style={styles.inlineMetadata}>
                                            <Unicon name="people-outline" size={14} colorVariant="tasks" />
                                            <Text style={styles.metadataText}>{teamName}</Text>
                                        </View>
                                    )
                                )}
                                {taskTime && (
                                    <View style={styles.inlineMetadata}>
                                        <Unicon name="time-outline" size={14} colorVariant="textSecondary" />
                                        <Text style={styles.metadataText}>
                                            {taskTime.toLocaleString('en-US', { hour: "numeric", minute: "2-digit" })}
                                        </Text>
                                    </View>
                                )}

                                {hasConflicts && (
                                    <Tooltip title={conflictTooltipText || 'Conflict'} appearOnTouch>
                                        <View style={styles.inlineMetadata}>
                                            <Unicon name="warning-outline" size={14} colorVariant="error" />
                                            <Text style={styles.statusText('error')}>Scheduling conflict</Text>
                                        </View>
                                    </Tooltip>
                                )}
                                {isCurrentUserAssignee && (!currentUserAssignment?.assignmentResponse || currentUserAssignment?.assignmentResponse === "NO_RESPONSE") && (
                                    <Text style={styles.statusText('warning')}>Awaiting acceptance</Text>
                                )}
                            </View>
                        </View>
                    </View>
                    {(showEventInfo || showTrainingInfo) && <View style={styles.contextSection}>
                        {showEventInfo && (
                            <TouchableOpacity testID={`TaskCard-event-${taskInstance.id}`} onPress={handleEventPress} style={styles.eventInfo} activeOpacity={0.7}>
                                <View style={styles.eventDot(eventColor)} />
                                <View style={styles.eventTextColumn}>
                                    <Text style={styles.contextLabel}>Event</Text>
                                    <Text style={styles.eventName}>{eventName}</Text>
                                    <Text style={styles.eventDate}>
                                        {eventDate?.toLocaleString('en-US', { weekday: 'short', month: "short", day: "numeric" })}
                                        {eventDate ? ` \u00b7 ${eventDate.toLocaleString('en-US', { hour: "numeric", minute: "2-digit" })}` : ''}
                                    </Text>
                                </View>
                                <Unicon name="chevron-forward" size={14} colorVariant="textSecondary" />
                            </TouchableOpacity>
                        )}
                        {showTrainingInfo && (
                            <TouchableOpacity testID={`TaskCard-training-${taskInstance.id}`} onPress={handleTrainingClassPress} style={styles.eventInfo} activeOpacity={0.7}>
                                <Unicon name="school-outline" size={16} colorVariant="training" />
                                <View style={styles.eventTextColumn}>
                                    <Text style={styles.contextLabel}>Training</Text>
                                    <Text style={styles.eventName}>{trainingClassInstance?.Class?.name}</Text>
                                    <Text style={styles.eventDate}>
                                        {trainingClassInstance?.scheduledAt
                                            ? new Date(trainingClassInstance.scheduledAt).toLocaleString('en-US', { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
                                            : 'Not scheduled'}
                                    </Text>
                                </View>
                                <Unicon name="chevron-forward" size={14} colorVariant="textSecondary" />
                            </TouchableOpacity>
                        )}
                    </View>}
                    {!!taskInstance?.additionalDetails && (
                        <View style={styles.detailsSection}>
                            <Text style={styles.detailsLabel}>Details</Text>
                            <Text style={styles.detailsText}>{taskInstance.additionalDetails}</Text>
                        </View>
                    )}
                    <View style={styles.assignmentRow}>
                        <View style={styles.assignmentSummary}>
                            <Text style={styles.contextLabel}>Assignees</Text>
                            <Text style={styles.assignmentCount(needsMoreAssignees)}>
                                {currentAssignees} of {assigneesNeeded} filled
                            </Text>
                        </View>
                        <View style={styles.assigneeControl}>
                            <SheetSelector
                                title={taskInstance?.Task?.title + " - Assignees"}
                                iconSize={20}
                                // Care tasks assign only through
                                // follow_up_assign_task on the occurrence
                                // screen — direct TaskInstancePeople writes
                                // can't bypass claim/assignment rules (§10.5).
                                disabled={!isCurrentUserAssigner || isCareTask}
                                multiple
                                forceModal
                                fromScreen={fromScreen}
                                redirectScreen={(assignees) => assigneesNeeded >= assignees.length ? null : "AssigneePositionsModal"}
                                addScreenName={loggedInUser?.can?.("Create People") ? "/people/create" : null}
                                type="people"
                                name="Assignees"
                                recents={recentAssignees}
                                peopleSubset={assigneeTeam?.Members}
                                subsetLabel={taskInstance?.VolunteerTeam?.name}
                                id={taskInstance?.id}
                                value={taskInstance?.Assignees || []}
                                triggerTooltip={assigneeNamesTooltip}
                                errorFn={(item) => {
                                    const errors = [];
                                    if (item.BlackoutPeriods?.length > 0) {
                                        const isInBlackoutPeriod = item.BlackoutPeriods?.some(b => {
                                            const due = new Date(taskInstance?.dueDate);
                                            return due >= new Date(b.startDate) && due <= new Date(b.endDate);
                                        });
                                        if (isInBlackoutPeriod) errors.push("This date is blacked out");
                                    }
                                    const conflicts = getConflictingTaskTitles(taskInstance, item.id);
                                    if (conflicts.length > 0) errors.push(`Scheduling conflict with ${conflicts.join(', ')}`);
                                    return errors;
                                }}
                                onChange={handleAssigneeChange}
                            >
                                <View style={styles.assigneesContainer}>
                                    {sortedAssignees.length > 0 ? assigneeAvatars : emptyAssigneeSlot}
                                </View>
                            </SheetSelector>
                        </View>
                    </View>

                    {showButtons && (
                        <View style={styles.buttonRow}>
                            {showResponseButtons && (
                                <>
                                    {(!currentUserAssignment.assignmentResponse || currentUserAssignment.assignmentResponse === "NO_RESPONSE" || currentUserAssignment.assignmentResponse === "DECLINED") && (
                                        <SolidButton
                                            text="Accept"
                                            iconName="checkmark"
                                            iconBefore
                                            size="sm"
                                            variant="muted"
                                            type="success"
                                            onPress={handleAccept}
                                            processing={updateAssigneeResponseMutation.isPending}
                                        />
                                    )}
                                    {currentUserAssignment.assignmentResponse === "ACCEPTED" && (
                                        <SolidButton
                                            text={isCareTask ? "Open & Complete" : "Mark as Completed"}
                                            iconName="checkmark-circle"
                                            iconBefore
                                            size="sm"
                                            variant="muted"
                                            type="success"
                                            onPress={handleMarkCompleted}
                                            processing={completionStatusMutation.isPending}
                                        />
                                    )}
                                    {(!currentUserAssignment.assignmentResponse || currentUserAssignment.assignmentResponse === "NO_RESPONSE" || currentUserAssignment.assignmentResponse === "ACCEPTED") && (
                                        <SolidButton
                                            text={currentUserAssignment.assignmentResponse === "ACCEPTED" ? "Change to Decline" : "Decline"}
                                            size="sm"
                                            variant="muted"
                                            type="error"
                                            iconName="close"
                                            iconBefore
                                            onPress={handleDeclinePress}
                                            processing={updateAssigneeResponseMutation.isPending}
                                        />
                                    )}
                                </>
                            )}
                            {showSelfAssign && (
                                <SolidButton
                                    text="Assign to me"
                                    iconName="person-add-outline"
                                    iconBefore
                                    size="sm"
                                    variant="muted"
                                    type="primary"
                                    onPress={handleSelfAssign}
                                />
                            )}
                            {actions.map((action, index) => (
                                <SolidButton
                                    key={index}
                                    text={action.label}
                                    size="sm"
                                    variant="muted"
                                    iconName={action.icon || undefined}
                                    iconBefore
                                    type={action.color || "primary"}
                                    onPress={action.fn}
                                />
                            ))}
                        </View>
                    )}
                </View>
            </CardContainer>

            <SheetModal
                isOpen={declineModalOpen}
                onClose={() => setDeclineModalOpen(false)}
                title="Decline Task"
                stayOpenOnBlur
                snapPoints={[300, 520]}
                loading={updateAssigneeResponseMutation.isPending}
                confirmText="Confirm"
                onConfirm={handleDeclineConfirm}
            >
                <InputSection>
                    <Input
                        title="Reason/Comment"
                        helpText="Optionally provide a reason for declining this task - this helps schedulers and team leaders improve future scheduling."
                        value={declineReason}
                        onUpdate={setDeclineReason}
                    />
                    {taskInstance?.Task?.allowForwarding && (
                        <View>
                            <SheetSelector
                                addScreenName={loggedInUser?.can?.("Create People") ? "people/create" : null}
                                type="people"
                                name="Forward Task"
                                fromScreen={fromScreen}
                                value={forwardTo}
                                excludeIds={[loggedInUser?.id]}
                                errorFn={(item) => {
                                    return taskInstance?.Assignees?.map(a => a.id).includes(item.id) ? ["Already Assigned"] : [];
                                }}
                                onChange={(selected_item) => {
                                    setForwardTo(selected_item);
                                }}
                            />
                            <Text style={styles.forwardHelpText}>For this task, you are allowed to forward assignment to someone else.</Text>
                        </View>
                    )}
                </InputSection>
            </SheetModal>

            <SortableList
                helpText="Reorder assignees to change their priority. Anyone labeled 'Primary' will be assigned first. If any of the 'Primary' assignees decline, the next 'Backup' will be notified and assigned."
                isOpen={openAssigneePositionsModal}
                close={() => setOpenAssigneePositionsModal(false)}
                onConfirm={(data) => {
                    const updatedAssignees = data.map((assignee, i) => ({ ...assignee, position: i }));
                    updateAssigneesMutation.mutate({ ...pendingAssigneeUpdate, newAssignees: updatedAssignees, notifyAll });
                }}
                items={[...(pendingAssigneeUpdate?.newAssignees || [])].sort((a, b) => a.position - b.position)}
                title="Order Assignees"
                itemContent={(item) => <Text style={styles.assigneeText()}>{item?.firstName + " " + item?.lastName}</Text>}
                itemHeight={50}
                labels={pendingAssigneeUpdate?.newAssignees?.map((assignee, i) => i < assigneesNeeded ? <Text key={i} style={styles.assigneeChipText("success")}>Primary</Text> : <Text key={i} style={styles.assigneeChipText("warning")}>Backup</Text>)}
                footer={(
                    <View style={styles.notifyAllRow}>
                        <Toggle value={notifyAll} onValueChange={setNotifyAll} />
                        <Text style={styles.notifyAllText}>Notify all assignees regardless of position</Text>
                    </View>
                )}
            />
        </>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows, spacing, typography }) => ({
    card: (variant) => ({
        marginHorizontal: variant === 'default' ? spacing.xs : 0,
        marginBottom: variant === 'embedded' ? 0 : spacing.md,
        backgroundColor: variant === 'default' ? colors.surface : 'transparent',
        borderWidth: variant === 'alternate' || variant === 'outlined' ? 1 : 0,
        borderColor: colors.border,
        borderRadius: variant === 'alternate' || variant === 'outlined' ? 10 : 3,
        boxShadow: shadows.none,
    }),
    cardInner: (embedded) => ({
        padding: embedded ? 0 : spacing.md,
        gap: spacing.sm,
        minWidth: 0,
    }),
    recordHeader: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: spacing.sm,
    },
    headerText: {
        flex: 1,
        minWidth: 0,
        gap: spacing.xs,
    },
    statusLine: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: spacing.sm,
        rowGap: spacing.xs,
    },
    dueText: {
        fontSize: typography.micro,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    contentRow: {
        flexDirection: 'row',
    },
    leftContent: {
        flex: 1,
        gap: 6,
    },
    titleLine: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 8,
    },
    taskTitle: {
        flex: 1,
        fontSize: 16,
        fontFamily: fonts.heading,
        color: colors.text,
        lineHeight: 20,
        _web: {
            _hover: {
                textDecorationLine: "underline",
            },
        },
    },
    taskTitleCompleted: {
        textDecorationLine: "line-through",
        color: colors.textSecondary,
    },
    enrolleeRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        alignSelf: "flex-start",
    },
    enrolleeText: {
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
    },
    metadataRow: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap",
        columnGap: spacing.md,
        rowGap: spacing.sm,
    },
    inlineMetadata: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs,
        flexShrink: 1,
    },
    metadataText: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        flexShrink: 1,
    },
    metadataLinkText: {
        _web: {
            _hover: {
                textDecorationLine: "underline",
            },
        },
    },
    statusText: (colorVariant) => ({
        fontSize: typography.micro,
        fontFamily: fonts.heading,
        color: colors[colorVariant],
    }),
    buttonRow: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap",
        gap: spacing.sm,
        minWidth: 0,
    },
    contextSection: {
        gap: spacing.sm,
    },
    eventInfo: {
        minWidth: 0,
        flexDirection: "row",
        alignItems: 'center',
        gap: spacing.sm,
        paddingVertical: spacing.xs,
    },
    eventTextColumn: {
        flex: 1,
        flexDirection: "column",
        gap: 2,
    },
    contextLabel: {
        fontSize: typography.micro,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    eventDot: (color) => ({
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: color ?? colors.primary,
    }),
    eventName: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.text,
        _web: {
            _hover: {
                textDecorationLine: "underline",
            },
        },
    },
    eventDate: {
        fontSize: 12,
        color: colors.textSecondary,
        fontFamily: fonts.body,
    },
    assigneeText: () => ({
        color: colors.text,
        fontSize: 15,
        paddingLeft: 10,
        fontFamily: fonts.heading,
    }),
    assigneeChipText: (colorVariant) => ({
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors[colorVariant],
    }),
    forwardHelpText: {
        color: colors.textSecondary,
        marginTop: 8,
        fontSize: 12,
        fontStyle: "italic",
    },
    assignmentRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: spacing.sm,
        paddingTop: spacing.sm,
        borderTopWidth: 1,
        borderTopColor: colors.borderSubtle,
    },
    assignmentSummary: {
        gap: 2,
    },
    assignmentCount: (needsAssignees) => ({
        fontSize: typography.caption,
        fontFamily: fonts.heading,
        color: needsAssignees ? colors.warning : colors.text,
        fontVariant: ['tabular-nums'],
    }),
    assigneeControl: {
        maxWidth: '100%',
        justifyContent: 'center',
    },
    assigneesContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: "center",
        gap: spacing.sm,
        pointerEvents: "none",
    },
    avatarStack: {
        flexDirection: "row",
        alignItems: "center",
    },
    avatarWrapper: {
        borderRadius: 20,
    },
    moreAvatars: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: colors.primary,
        alignItems: "center",
        justifyContent: "center",
    },
    moreAvatarsText: {
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors.onPrimary,
    },
    emptyAvatar: (isCurrentUserAssigner) => ({
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: colors.surfaceVariant,
        alignItems: "center",
        justifyContent: "center",
        opacity: isCurrentUserAssigner ? 1 : 0.5,
    }),
    detailsSection: {
        gap: spacing.xs,
    },
    detailsLabel: {
        fontSize: 10,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
        textTransform: "uppercase",
        letterSpacing: 0.5,
    },
    detailsText: {
        fontSize: 13,
        color: colors.text,
        lineHeight: 18,
    },
    notifyAllRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        paddingHorizontal: 12,
        paddingTop: 10,
        paddingBottom: 32,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        marginTop: 8,
    },
    notifyAllText: {
        fontSize: 13,
        color: colors.textSecondary,
        fontFamily: fonts.body,
        flex: 1,
    },
}));

export default React.memo(TaskCard);
